import { DeleteOutlined } from '@ant-design/icons'
import { Button, Card, Input, Space, Switch, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useForms } from '../../../contexts/FormContext'
import {
  FormItem as FormItemModel,
  FormItemTypes,
} from '../../../models/FormItem'
import { FormPage } from '../../../models/FormPage'
import { Form } from '../Form'
import AssociatedItemSelection from './AssociatedItemSelection'
import { getIconByTypeId } from './utils/IconHelper'

const { Text } = Typography

interface Props {
  item: FormItemModel
  page: FormPage
}

export const FormItem = ({ item, page }: Props) => {
  const { form, updateForm } = useForms()

  const [isRequired, setIsRequired] = useState<boolean>(item.required ?? true)
  const [isCommentableByManager, setIsCommentableByManager] = useState<boolean>(
    item.isCommentableByManager ?? false
  )
  const [isAnswerableByManager, setIsAnswerableByManager] = useState<boolean>(
    item.isAnswerableByManager ?? false
  )
  const [associatedItems, setAssociatedItems] = useState<
    FormItemModel[] | undefined
  >(item.associatedItems)

  const [question, setQuestion] = useState<string>(item.question)
  const [description, setDescription] = useState<string | undefined>(
    item.description
  )

  const handleUpdateItem = () => {
    const updatedForm = {
      ...form,
      pages: form.pages.map((p) =>
        p.id === page.id
          ? {
              ...p,
              items: p.items.map((i) =>
                i.id === item.id
                  ? {
                      ...i,
                      question: question,
                      description: description,
                      required: isRequired,
                      isCommentableByManager: isCommentableByManager,
                      isAnswerableByManager: isAnswerableByManager,
                      associatedItems: associatedItems,
                    }
                  : i
              ),
            }
          : p
      ),
    }
    updateForm(updatedForm)
  }

  const handleDeleteItem = () => {
    const updatedForm = {
      ...form,
      pages: form.pages.map((p) =>
        p.id === page.id
          ? {
              ...p,
              items: p.items.filter((i) => i.id !== item.id),
            }
          : p
      ),
    }
    updateForm(updatedForm)
  }

  useEffect(() => {
    handleUpdateItem()
  }, [
    isRequired,
    isCommentableByManager,
    isAnswerableByManager,
    question,
    description,
    associatedItems,
  ])

  return (
    <Card>
      <Space vertical className="w-full">
        <Space className="w-full space-between">
          <Space>
            {getIconByTypeId(item.type.id)} {item.type.name}
          </Space>
          <Space>
            {item.type.requirable && (
              <Space>
                <Text type="secondary">Requis</Text>
                <Switch
                  size="small"
                  defaultChecked={isRequired}
                  onChange={() => setIsRequired(!isRequired)}
                />
              </Space>
            )}
            <Button
              icon={<DeleteOutlined />}
              type="text"
              size="small"
              danger
              onClick={handleDeleteItem}
            />
          </Space>
        </Space>
        <Space className="w-full space-between">
          <Text type="secondary">Le manager peut:</Text>
          <Space>
            <Text type="secondary">Commenter</Text>
            <Switch
              size="small"
              defaultChecked={isCommentableByManager}
              onChange={() =>
                setIsCommentableByManager(!isCommentableByManager)
              }
            />
          </Space>
          <Space>
            <Text type="secondary">Répondre</Text>
            <Switch
              size="small"
              defaultChecked={isAnswerableByManager}
              onChange={() => setIsAnswerableByManager(!isAnswerableByManager)}
            />
          </Space>
        </Space>
        {item.type.id !== FormItemTypes.Spacer && (
          <>
            <Input
              value={question}
              size="large"
              placeholder="Titre"
              onChange={(e) => setQuestion(e.target.value)}
            />
            <Input
              value={description}
              size="middle"
              placeholder="Description"
              onChange={(e) => setDescription(e.target.value)}
            />
            {item.type.optionable && <Form.ItemOptionEditor formItem={item} />}
          </>
        )}
        {item.type.id !== FormItemTypes.Title &&
          item.type.id !== FormItemTypes.Spacer && (
            <AssociatedItemSelection
              item={item}
              onFormItemSelected={(item) => {
                setAssociatedItems(item ? [item] : [])
              }}
            />
          )}
      </Space>
    </Card>
  )
}

export type FormItemType = { FormItem: typeof FormItem }
